'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';

interface PaymentSectionProps {
  onBack: () => void;
  onPaymentSuccess: () => void;
}

type PaymentMethod = 'upi' | 'card';
type PaymentStatus = 'idle' | 'loading' | 'success' | 'error';

type UpiFormData = {
  upiId: string;
};

type CardFormData = {
  cardNumber: string;
  cardName: string;
  expiry: string;
  cvv: string;
};

export default function PaymentSection({ onBack, onPaymentSuccess }: PaymentSectionProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('upi');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');

  const upiForm = useForm<UpiFormData>({ mode: 'onBlur' });
  const cardForm = useForm<CardFormData>({ mode: 'onBlur' });

  // BACKEND INTEGRATION POINT: POST /api/payments/initiate — integrate Razorpay/PayU
  const handleUpiSubmit = async (data: UpiFormData) => {
    setPaymentStatus('loading');
    await new Promise((r) => setTimeout(r, 2000));
    console.log('UPI payment data:', data);
    setPaymentStatus('success');
    setTimeout(() => onPaymentSuccess(), 1200);
  };

  // BACKEND INTEGRATION POINT: POST /api/payments/card — integrate payment gateway
  const handleCardSubmit = async (data: CardFormData) => {
    setPaymentStatus('loading');
    await new Promise((r) => setTimeout(r, 2000));
    console.log('Card payment data:', data);
    setPaymentStatus('success');
    setTimeout(() => onPaymentSuccess(), 1200);
  };

  const formatCardNumber = (value: string) => {
    return value
      .replace(/\D/g, '')
      .slice(0, 16)
      .replace(/(.{4})/g, '$1 ')
      .trim();
  };

  const formatExpiry = (value: string) => {
    return value
      .replace(/\D/g, '')
      .slice(0, 4)
      .replace(/^(\d{2})(\d)/, '$1/$2');
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 fade-in">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className="w-9 h-9 rounded-lg bg-muted hover:bg-border flex items-center justify-center transition-colors"
          aria-label="Go back to documents"
        >
          <Icon name="ArrowLeftIcon" size={18} variant="outline" className="text-foreground" />
        </button>
        <div>
          <h2 className="text-xl font-700 text-foreground">Verification Fee Payment</h2>
          <p className="text-muted-foreground text-sm">One-time payment to activate your FCEP membership</p>
        </div>
      </div>

      {/* Amount Banner */}
      <div className="bg-primary rounded-xl p-5 mb-6 flex items-center justify-between">
        <div>
          <p className="text-blue-200 text-sm">Amount to Pay</p>
          <p className="text-white text-3xl font-800">₹500.00</p>
          <p className="text-blue-200 text-xs mt-1">FCEP Document Verification Fee</p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 bg-white/10 rounded-lg px-3 py-1.5 mb-2">
            <Icon name="ShieldCheckIcon" size={14} variant="solid" className="text-accent" />
            <span className="text-white text-xs font-500">Secure Payment</span>
          </div>
          <p className="text-blue-200 text-xs">One-time fee only</p>
        </div>
      </div>

      {/* Payment Method Tabs */}
      <div className="flex gap-3 mb-6">
        <button
          type="button"
          className={`payment-tab flex-1 flex items-center justify-center gap-2 ${
            paymentMethod === 'upi' ? 'active' : 'inactive'
          }`}
          onClick={() => setPaymentMethod('upi')}
        >
          <Icon name="DevicePhoneMobileIcon" size={18} variant="outline" />
          UPI Payment
        </button>
        <button
          type="button"
          className={`payment-tab flex-1 flex items-center justify-center gap-2 ${
            paymentMethod === 'card' ? 'active' : 'inactive'
          }`}
          onClick={() => setPaymentMethod('card')}
        >
          <Icon name="CreditCardIcon" size={18} variant="outline" />
          Debit / Credit Card
        </button>
      </div>

      {/* Payment Status Success */}
      {paymentStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-center gap-3 mb-4">
          <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-secondary flex-shrink-0" />
          <div>
            <p className="text-secondary font-700 text-sm">Payment Successful!</p>
            <p className="text-green-700 text-xs">₹500 received. Redirecting to confirmation...</p>
          </div>
        </div>
      )}

      {paymentStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 mb-4">
          <Icon name="ExclamationCircleIcon" size={18} variant="solid" className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-danger text-sm">
            Payment failed. Please check your details and try again or contact support.
          </p>
        </div>
      )}

      {/* UPI Payment Form */}
      {paymentMethod === 'upi' && (
        <form onSubmit={upiForm.handleSubmit(handleUpiSubmit)} noValidate>
          {/* QR Code Placeholder */}
          <div className="bg-muted border border-border rounded-xl p-6 mb-5 text-center">
            <div className="w-36 h-36 bg-white border-2 border-border rounded-xl mx-auto mb-3 flex items-center justify-center">
              <div className="grid grid-cols-3 gap-1 p-3 w-full h-full">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div
                    key={`qr-cell-${i + 1}`}
                    className={`rounded-sm ${
                      [0, 1, 2, 3, 5, 6, 7, 8].includes(i) ? 'bg-foreground' : 'bg-white'
                    }`}
                  />
                ))}
              </div>
            </div>
            <p className="text-sm font-600 text-foreground">Scan QR Code to Pay</p>
            <p className="text-xs text-muted-foreground mt-1">
              Use any UPI app — PhonePe, GPay, Paytm, BHIM
            </p>
            <div className="mt-3 bg-white border border-border rounded-lg px-3 py-2 inline-flex items-center gap-2">
              <Icon name="DevicePhoneMobileIcon" size={14} variant="outline" className="text-primary" />
              <span className="text-sm font-600 text-primary">fcep.cmx@upi</span>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground font-500 px-2">OR enter UPI ID manually</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* UPI ID Input */}
          <div className="mb-5">
            <label htmlFor="upiId" className="form-label">
              Your UPI ID <span className="text-danger">*</span>
            </label>
            <p className="form-helper mb-2">
              Enter your UPI ID to initiate payment (e.g. yourname@paytm, 9876543210@upi)
            </p>
            <input
              id="upiId"
              type="text"
              placeholder="yourname@paytm or 9876543210@upi"
              className={`input-field ${upiForm.formState.errors.upiId ? 'error' : ''}`}
              {...upiForm.register('upiId', {
                required: 'UPI ID is required',
                pattern: {
                  value: /^[\w.\-_]{2,256}@[a-zA-Z]{2,64}$/,
                  message: 'Enter a valid UPI ID (e.g. name@paytm)',
                },
              })}
            />
            {upiForm.formState.errors.upiId && (
              <p className="form-error">
                <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                {upiForm.formState.errors.upiId.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={paymentStatus === 'loading' || paymentStatus === 'success'}
            className="btn-primary w-full justify-center text-base disabled:opacity-70 disabled:cursor-not-allowed"
            style={{ padding: '0.875rem 1.5rem' }}
          >
            {paymentStatus === 'loading' ? (
              <>
                <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                Processing Payment...
              </>
            ) : (
              <>
                <Icon name="DevicePhoneMobileIcon" size={20} variant="outline" />
                Pay ₹500 via UPI
              </>
            )}
          </button>
        </form>
      )}

      {/* Card Payment Form */}
      {paymentMethod === 'card' && (
        <form onSubmit={cardForm.handleSubmit(handleCardSubmit)} noValidate>
          <div className="space-y-4">
            {/* Card Number */}
            <div>
              <label htmlFor="cardNumber" className="form-label">
                Card Number <span className="text-danger">*</span>
              </label>
              <div className="relative">
                <input
                  id="cardNumber"
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  className={`input-field pr-12 ${cardForm.formState.errors.cardNumber ? 'error' : ''}`}
                  {...cardForm.register('cardNumber', {
                    required: 'Card number is required',
                    validate: (v) =>
                      v.replace(/\s/g, '').length === 16 || 'Enter a valid 16-digit card number',
                    onChange: (e) => {
                      e.target.value = formatCardNumber(e.target.value);
                    },
                  })}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                  <div className="w-7 h-5 rounded bg-blue-600 opacity-70" />
                  <div className="w-7 h-5 rounded bg-red-500 opacity-70" />
                </div>
              </div>
              {cardForm.formState.errors.cardNumber && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {cardForm.formState.errors.cardNumber.message}
                </p>
              )}
            </div>

            {/* Card Name */}
            <div>
              <label htmlFor="cardName" className="form-label">
                Name on Card <span className="text-danger">*</span>
              </label>
              <input
                id="cardName"
                type="text"
                placeholder="As printed on your card"
                className={`input-field ${cardForm.formState.errors.cardName ? 'error' : ''}`}
                {...cardForm.register('cardName', {
                  required: 'Name on card is required',
                  minLength: { value: 3, message: 'Enter the full name on your card' },
                })}
              />
              {cardForm.formState.errors.cardName && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {cardForm.formState.errors.cardName.message}
                </p>
              )}
            </div>

            {/* Expiry + CVV */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="expiry" className="form-label">
                  Expiry Date <span className="text-danger">*</span>
                </label>
                <input
                  id="expiry"
                  type="text"
                  placeholder="MM/YY"
                  maxLength={5}
                  className={`input-field ${cardForm.formState.errors.expiry ? 'error' : ''}`}
                  {...cardForm.register('expiry', {
                    required: 'Expiry date is required',
                    pattern: {
                      value: /^(0[1-9]|1[0-2])\/\d{2}$/,
                      message: 'Enter valid expiry (MM/YY)',
                    },
                    onChange: (e) => {
                      e.target.value = formatExpiry(e.target.value);
                    },
                  })}
                />
                {cardForm.formState.errors.expiry && (
                  <p className="form-error">
                    <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                    {cardForm.formState.errors.expiry.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="cvv" className="form-label">
                  CVV <span className="text-danger">*</span>
                </label>
                <div className="relative">
                  <input
                    id="cvv"
                    type="password"
                    placeholder="•••"
                    maxLength={4}
                    className={`input-field ${cardForm.formState.errors.cvv ? 'error' : ''}`}
                    {...cardForm.register('cvv', {
                      required: 'CVV is required',
                      pattern: {
                        value: /^\d{3,4}$/,
                        message: 'Enter valid 3 or 4 digit CVV',
                      },
                    })}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Icon name="LockClosedIcon" size={14} variant="outline" className="text-muted-foreground" />
                  </div>
                </div>
                {cardForm.formState.errors.cvv && (
                  <p className="form-error">
                    <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                    {cardForm.formState.errors.cvv.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Security Note */}
          <div className="flex items-center gap-2 mt-4 mb-5 p-3 bg-green-50 border border-green-100 rounded-lg">
            <Icon name="ShieldCheckIcon" size={16} variant="solid" className="text-secondary flex-shrink-0" />
            <p className="text-green-700 text-xs">
              Your card details are encrypted and processed securely. FCEP does not store your card information.
            </p>
          </div>

          <button
            type="submit"
            disabled={paymentStatus === 'loading' || paymentStatus === 'success'}
            className="btn-primary w-full justify-center text-base disabled:opacity-70 disabled:cursor-not-allowed"
            style={{ padding: '0.875rem 1.5rem' }}
          >
            {paymentStatus === 'loading' ? (
              <>
                <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                Processing Payment...
              </>
            ) : (
              <>
                <Icon name="CreditCardIcon" size={20} variant="outline" />
                Pay ₹500 via Card
              </>
            )}
          </button>
        </form>
      )}

      {/* Accepted Payments */}
      <div className="mt-5 pt-5 border-t border-border flex items-center justify-center gap-3 flex-wrap">
        {['PhonePe', 'GPay', 'Paytm', 'BHIM', 'Visa', 'Mastercard'].map((method) => (
          <span
            key={`pay-method-${method.toLowerCase()}`}
            className="text-xs text-muted-foreground bg-muted border border-border rounded px-2 py-1 font-500"
          >
            {method}
          </span>
        ))}
      </div>
    </div>
  );
}